import {
  type AppDate,
  calendarLabel,
  dayjs,
  guessTz,
  ISO_8601,
  isAppDate,
  isValidTimezone,
  nowInTz,
  parseLenient,
  parseStrictIso,
} from './date';
import { convertUtcToLocal, convertUtcToRelevantTimezone } from './date-conversion';
import { SCHEDULE_SLOT_DEFINITIONS } from './schedule-types';
import { generateScheduleDateString } from './utils';

// Moved to the leaf module date-conversion.ts (now a shim over date.ts); re-exported
// here so every existing 'src/helper/time-utils' import keeps working unchanged.
export { convertUtcToLocal, convertUtcToRelevantTimezone };

/** Returns the current time in the given IANA timezone, or local time if absent/unrecognised. */
export const getNowInTimezone = nowInTz;

/**
 * The zone a timestamp is read in: the record's own zone when it carries one,
 * otherwise the viewer's. An unrecognised IANA name is treated as absent so a
 * stale or misspelled payload zone degrades to the viewer rather than throwing.
 */
const resolveDisplayTimezone = (timezone?: string): string =>
  timezone && isValidTimezone(timezone) ? timezone : guessTz();

/**
 * Three-letter zone abbreviation (CST, CDT, PST…) for the given zone, read at a
 * specific instant. The instant matters: America/Chicago is CST in January and
 * CDT in July, so an abbreviation is only correct relative to the moment it
 * labels. Defaults to now for surfaces that label a whole view rather than one
 * timestamp. Zones with no lettered abbreviation yield moment's numeric form
 * (Asia/Dubai → `+04`).
 */
export const getTimezoneAbbreviation = (timezone?: string, atUtcInstant?: string): string => {
  const zone = resolveDisplayTimezone(timezone);
  const instant = atUtcInstant ? parseStrictIso(atUtcInstant) : dayjs.utc();
  if (!instant?.isValid()) return '';
  return instant.tz(zone).format('z');
};

/**
 * Zone abbreviation for a whole calendar day (`YYYY-MM-DD`) in the given zone.
 * Read at local midday so a DST transition — which lands at 02:00 — never
 * reports the side of the change the day is mostly not on.
 */
export const getTimezoneAbbreviationForDate = (date: string, timezone?: string): string => {
  if (!date) return '';
  const zone = resolveDisplayTimezone(timezone);
  const midday = dayjs.tz(`${date} 12:00`, 'YYYY-MM-DD HH:mm', zone);
  if (!midday.isValid()) return '';
  return midday.format('z');
};

/**
 * A UTC instant rendered in the record's own (property) zone with the zone
 * abbreviation appended. If the zone is missing or unrecognised, the viewer's own
 * zone is used instead and its abbreviation is still appended. For
 * property-anchored times only — promises about the property such as appointments
 * and availability windows. Not for records of a moment (created / approved /
 * sent): those read in the viewer's own zone with no zone label
 * ("Foresight: Date & Time Format"). Date-only "wall date" fields (due dates)
 * must NOT go through here: they carry no instant, so they belong to no zone and
 * take no label.
 */
export const formatInTimezoneWithLabel = (
  dateString: string,
  timezone: string | undefined,
  format: string,
  emptyValue: string = '-',
): string => {
  if (!dateString) return emptyValue;
  const instant = parseStrictIso(dateString);
  if (!instant?.isValid()) return emptyValue;
  return instant.tz(resolveDisplayTimezone(timezone)).format(`${format} z`);
};

/** The zone's name as `Intl` spells it at that instant ('' if it has none). */
const intlZoneName = (timezone: string, epochMs: number, style: 'short' | 'long'): string => {
  try {
    return (
      new Intl.DateTimeFormat('en-US', { timeZone: timezone, timeZoneName: style })
        .formatToParts(epochMs)
        .find((part) => part.type === 'timeZoneName')?.value ?? ''
    );
  } catch {
    return '';
  }
};

export type PropertyZoneTimeText = {
  /** Shown in the hover: `Jul 1, 2026, 4:49 AM CDT`. */
  label: string;
  /** Read by screen readers, with the zone's full name: `Jul 1, 2026, 4:49 AM Central Daylight Time`. */
  spoken: string;
};

/**
 * The same instant as a record of a moment, read in the property's zone — the hover
 * on a viewer-zone "Created" time ("Foresight: Date & Time Format"). Format Standard
 * `MMM D, YYYY, h:mm A ZZZ`, with `:00` minutes omitted.
 *
 * STRICT, unlike formatInTimezoneWithLabel: a missing or unrecognised zone returns
 * null rather than falling back to the viewer's zone, because a guessed zone shown as
 * "the property's time" is exactly what the rule forbids. Callers show no hover then.
 */
export const formatPropertyZoneTime = (
  dateString: string | null | undefined,
  timezone: string | null | undefined,
): PropertyZoneTimeText | null => {
  if (!dateString || !timezone || !isValidTimezone(timezone)) return null;
  const instant = parseStrictIso(dateString);
  if (!instant?.isValid()) return null;

  const zoned = instant.tz(timezone);
  const clock = zoned.format(zoned.minute() === 0 ? 'MMM D, YYYY, h A' : 'MMM D, YYYY, h:mm A');
  // The app-wide abbreviation table first, so the hover matches every other zone label.
  const abbreviation = zoned.format('z') || intlZoneName(timezone, instant.valueOf(), 'short');
  const fullName = intlZoneName(timezone, instant.valueOf(), 'long') || abbreviation;
  if (!abbreviation) return null;

  return { label: `${clock} ${abbreviation}`, spoken: `${clock} ${fullName}` };
};

/**
 * A date-only "wall date" — a due date — rendered as the day it names. The wire
 * form is a UTC-midnight instant, so reading it in the viewer's zone lands on the
 * previous day anywhere west of UTC. The day itself is the value: it belongs to no
 * zone, is not converted, and takes no zone label.
 */
export const formatWallDate = (
  dateString: string,
  format: string,
  emptyValue: string = '',
): string => {
  if (!dateString) return emptyValue;
  const wallDate = parseStrictIso(dateString);
  if (!wallDate?.isValid()) return emptyValue;
  return wallDate.format(format);
};

/**
 * Appends the zone abbreviation to a string another formatter already rendered
 * in that zone. For surfaces whose formatter is shared with callers that take no
 * label (schedule ranges, pre-derived timeline strings) — a placeholder such as
 * `-` is passed through unlabelled.
 */
export const appendTimezoneLabel = (
  formattedValue: string,
  timezone?: string,
  atUtcInstant?: string,
): string => {
  if (!formattedValue || formattedValue === '-') return formattedValue;
  const abbreviation = getTimezoneAbbreviation(timezone, atUtcInstant);
  return abbreviation ? `${formattedValue} ${abbreviation}` : formattedValue;
};

export const convertUtcToLocalHumanizeFormat = (dateString: string) => {
  if (dateString) {
    const dateInLocal = parseStrictIso(dateString);
    // moment humanized a NaN duration for unparseable input — pinned lowercase literal.
    if (!dateInLocal) return 'Invalid date';
    // Algebraically identical to duration(date - now).humanize(true), without the
    // two-reads-of-"now" race the duration form had.
    return dateInLocal.from(dayjs());
  }
  return 'Invalid Date';
};

export const calculateTimeDiff = (startedAt: AppDate, endedAt: AppDate) => {
  const isDateLike = (value: unknown) => isAppDate(value);
  if (!isDateLike(startedAt) || !isDateLike(endedAt)) {
    return '-';
  }

  // Epoch coercion tolerates mixed moment/AppDate pairs (instance .diff() would
  // object-parse the foreign kind). Components reproduce moment's truncation
  // toward zero (duration seconds/minutes) on the absolute difference.
  const startedAtMs = startedAt.valueOf();
  const endedAtMs = endedAt.valueOf();
  const sign = startedAtMs > endedAtMs ? '-' : '';

  const absMs = Math.abs(endedAtMs - startedAtMs);
  const totalHours = Math.floor(absMs / 3600000);
  const totalSeconds = Math.floor(absMs / 1000);
  const minutes = Math.floor(totalSeconds / 60) % 60;
  const seconds = totalSeconds % 60;

  const formattedHours = totalHours.toString().padStart(2, '0');
  const formattedMinutes = minutes.toString().padStart(2, '0');
  const formattedSeconds = seconds.toString().padStart(2, '0');

  return `${sign}${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
};

export const convertElapsedTimeInSeconds = (hours: number, minutes: number) => {
  // Convert hours and minutes to seconds
  const totalSeconds = hours * 3600 + minutes * 60;
  return totalSeconds;
};

export const formatTimeLabel = (time: number) => {
  if (time >= 60) {
    const minutes = time % 60;
    const hours = Math.floor(time / 60);
    return `${hours}hr ${minutes ? `${minutes}m` : ''}`;
  }
  return `${time}m`;
};

export const formattedTimeDiff = (totalTimeInSec: number, sign?: string) => {
  // Absolute input: component-of-abs equals abs-of-component under truncation, and
  // it sidesteps dayjs.duration's floor-based negative decomposition.
  const diff = dayjs.duration(Math.abs(totalTimeInSec), 's');

  const totalHours = Math.floor(diff.asHours());
  const minutes = diff.minutes();
  const seconds = diff.seconds();

  const formattedHours = totalHours?.toString()?.padStart(2, '0');
  const formattedMinutes = minutes?.toString()?.padStart(2, '0');
  const formattedSeconds = seconds?.toString()?.padStart(2, '0');

  return `${sign || ''}${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
};

export const formatSecondsToHourAndMinute = (totalTimeInSec: number, withSpacing?: boolean) => {
  const diff = dayjs.duration(Math.abs(totalTimeInSec || 0), 's');

  const totalHours = Math.floor(diff.asHours() || 0);
  const minutes = diff.minutes() || 0;

  const formattedHours = totalHours.toString().padStart(2, '0');
  const formattedMinutes = minutes.toString().padStart(2, '0');

  if (withSpacing) {
    return `${formattedHours} : ${formattedMinutes}`;
  }
  return `${formattedHours}:${formattedMinutes}`;
};

export const formattedDateDiff = (date: string, timezone: string) => {
  if (!date || !timezone || !isValidTimezone(timezone)) return '-';
  const time = convertUtcToRelevantTimezone<AppDate>(date, timezone, '', ISO_8601);
  if (typeof time === 'boolean' || !time?.isValid()) return '-';
  return time.format('MMM D, YYYY');
};

export const dateConverter = (date: string, timezone: string): string => {
  if (date) {
    const currentTime = dayjs();
    const logTime = convertUtcToRelevantTimezone<AppDate>(date, timezone, '', ISO_8601);
    if (typeof logTime !== 'boolean' && logTime?.isValid()) {
      const secondsDiff = currentTime.diff(logTime, 'seconds');

      // calendarLabel function entries return the FINAL string (moment's returned a
      // format string), so the branch formats explicitly. Omitted buckets (nextDay,
      // nextWeek) fall back to the locale defaults exactly as moment's calendar did.
      return calendarLabel(logTime, currentTime, {
        sameDay: (bucketedDate) =>
          bucketedDate.format(secondsDiff < 60 ? '[Just now]' : '[Today at] h:mm A'),
        lastDay: '[Yesterday at] h:mm A',
        lastWeek: 'MMM D [at] h:mm A',
        sameElse: 'MMM D [at] h:mm A',
      });
    }
  }
  return '';
};

export const formatActivityLogTooltip = (date: string, timezone?: string): string => {
  if (!date) return '';
  const hasValidTimezone = timezone && isValidTimezone(timezone);
  const time = hasValidTimezone
    ? convertUtcToRelevantTimezone<AppDate>(date, timezone, '', ISO_8601)
    : convertUtcToLocal<AppDate>(date);
  if (!time || typeof time === 'boolean' || !time.isValid()) return '';
  const now = hasValidTimezone ? nowInTz(timezone) : dayjs();
  if (time.isSame(now, 'day')) {
    return `Today at ${time.format('h:mm:ss A')}`;
  }
  return `${time.format('ddd, MMM D YYYY')} at ${time.format('h:mm:ss A')}`;
};

export const formatActivityLogDayLabel = (dayKey: string, timezone?: string): string => {
  if (!dayKey) return '';
  const hasValidTimezone = timezone && isValidTimezone(timezone);
  const todayKey = hasValidTimezone
    ? nowInTz(timezone).format('YYYY-MM-DD')
    : dayjs().format('YYYY-MM-DD');
  if (dayKey === todayKey) return 'Today';
  const parsed = dayjs(dayKey, 'YYYY-MM-DD');
  // moment rendered an unparseable key as the lowercase 'Invalid date'; keep it verbatim.
  return parsed.isValid() ? parsed.format('ddd, MMM D YYYY') : 'Invalid date';
};

export const formatRelativeTime = (date: string, timezone: string) => {
  // Guarded "now": moment's .tz(unknown) was a logged no-op that stayed on local
  // time; dayjs's would throw, so the zone is validated first (same fallback).
  const now = nowInTz(timezone);
  const convertedDate = convertUtcToRelevantTimezone<AppDate>(date, timezone, '', ISO_8601);

  if (typeof convertedDate === 'boolean' || !convertedDate.isValid()) {
    return 'Invalid Date';
  }

  const diffDays = now.diff(convertedDate, 'days');

  if (diffDays <= 7) {
    return convertedDate.from(now); // This will show "4 days ago", "2 days ago" etc
  }
  return convertedDate.format('MMM D, YYYY, h:mm A'); // Format: Dec 4, 2024, 8:50 AM
};

export const formattedScheduleDate = (
  startTime: string,
  endTime: string,
  // Widened in the TYPE only — the behaviour is unchanged. This lets a caller that
  // legitimately holds no zone (a trip card whose work order carries none) say so instead
  // of manufacturing one; per COM-655, '-' is the right answer for a record-anchored date
  // with no record zone.
  timezone: string | undefined,
  format: string,
) => {
  // `!timezone` joins the existing guard rather than adding a branch: `utcToZoned` already
  // returned false for a falsy zone, so both fell to the same '-' below. Hoisting it here
  // narrows the type for the two calls and states the case in one place.
  if (!startTime || !endTime || !timezone) {
    return '-';
  }

  const startTimeInTimezone = convertUtcToRelevantTimezone<AppDate>(
    startTime,
    timezone,
    '',
    ISO_8601,
  );
  const endTimeInTimezone = convertUtcToRelevantTimezone<AppDate>(endTime, timezone, '', ISO_8601);

  if (startTimeInTimezone && endTimeInTimezone) {
    return generateScheduleDateString(startTimeInTimezone, endTimeInTimezone, format);
  }

  return '-';
};

export const convertSecondsToHoursMinutes = (totalSeconds: number) => {
  const hours = Math.floor(totalSeconds / 3600);
  const remainingSecondsAfterHours = totalSeconds % 3600;
  const minutes = Math.floor(remainingSecondsAfterHours / 60);
  const seconds = remainingSecondsAfterHours % 60;

  return { hours, minutes, seconds };
};

export const convertHoursMinutesToSeconds = (hours: number, minutes: number) => {
  const hrs = hours * 3600;
  const mins = minutes * 60;

  return hrs + mins;
};

export const formatTwoDigitNumber = (num: number): string => {
  if (num < 10) {
    return num.toString().padStart(2, '0');
  }
  return num.toString();
};

export const formatDate = (dateString: string, format: string) => {
  if (!dateString) return '';
  const date = parseLenient(dateString);
  if (date?.isValid()) {
    return date?.format(format);
  }
  return '';
};

export const formatScheduleWithPeriod = (
  startTime: string,
  endTime: string,
  timezone: string,
): { date: string; time: string } => {
  if (!startTime || !endTime || !timezone) {
    return { date: '-', time: '-' };
  }

  const startTimeInTimezone = convertUtcToRelevantTimezone<AppDate>(
    startTime,
    timezone,
    '',
    ISO_8601,
  );
  const endTimeInTimezone = convertUtcToRelevantTimezone<AppDate>(endTime, timezone, '', ISO_8601);

  if (
    !startTimeInTimezone ||
    !endTimeInTimezone ||
    typeof startTimeInTimezone === 'boolean' ||
    typeof endTimeInTimezone === 'boolean'
  ) {
    return { date: '-', time: '-' };
  }

  const date = startTimeInTimezone.format('MMM DD, YYYY');

  const startHourFormatted = startTimeInTimezone.format('h A');
  const endHourFormatted = endTimeInTimezone.format('h A');
  const startH = startTimeInTimezone.hour();
  const endH = endTimeInTimezone.hour();
  const matchingSlot = SCHEDULE_SLOT_DEFINITIONS.find(
    (s) => s.startHour === startH && s.endHour === endH,
  );
  const time = matchingSlot
    ? `${matchingSlot.label} (${startHourFormatted} - ${endHourFormatted})`
    : `${startHourFormatted} - ${endHourFormatted}`;

  return { date, time };
};
